/**
 * Hush ghost wardrobe (sol61 batch-003 `wardrobe/source/types.ts`, adopted 2026-10-22).
 *
 * Rig-attached accessories for the Hush Nook cast drawn through the existing humanoid extras layers. The
 * catalog types (`WardrobeRecipe`) live outside `src/`, so the subset this module needs is copied here.
 */
import type { HumanoidAppearance, HumanoidExtras } from '../parts/humanoid/HumanoidTypes';
import type { DetailTier, PaletteId, Point2, StaticRecipe, Tokens, VisualBounds } from '../../../shared/assets/hushStatic/contracts';

export type { HumanoidAppearance, Point2, StaticRecipe, Tokens, VisualBounds };

/** Bump when construction changes so cached textures cannot keep old art (it is part of the appearance hash). */
export const REVISION = 'hush-wardrobe-candidate.1';

export interface WardrobeParams {
  readonly look: 'pip' | 'mallow';
  readonly pose: 'idle' | 'talk' | 'gesture';
  readonly propSupport: 'rig-if-available-else-nearby';
  readonly facePolicy: 'preserve-existing-rig-face';
}

/** Catalog `WardrobeRecipe` (`hush.ghost_wardrobe.v1` envelope). */
export interface WardrobeRecipe {
  readonly schema: 'hush.asset-recipe.v1';
  readonly id: string;
  readonly assetId: string;
  readonly generatorId: 'hush.ghost_wardrobe.v1';
  readonly seed: number;
  readonly paletteId: PaletteId;
  readonly detail: DetailTier;
  readonly params: WardrobeParams;
}

export interface WardrobeResources {
  ribbon: StaticRecipe;
  boat: StaticRecipe;
}

export interface HushWardrobeConfig {
  schema: 'hush.rig-accessory.v1';
  revision: typeof REVISION;
  recipe: WardrobeRecipe;
  tokens: Tokens;
  resources: WardrobeResources;
}

export type HushExtras = HumanoidExtras & { hushWardrobe?: HushWardrobeConfig };
export type HushAppearance = Omit<HumanoidAppearance, 'humanoid'> & { humanoid: HushExtras };

/** Affine SVG matrix `[a b c d e f]`. */
export type Matrix = readonly [number, number, number, number, number, number];

export interface AccessoryPiece {
  id: string;
  /** `head` draws in the classic-head space mapped by GhostRenderer's head fit; `worn` sits above clothing, below the front hand. */
  slot: 'head' | 'worn';
  body: string;
  coordinateSpace: 'classic-head' | 'body-rig';
  paintBounds: VisualBounds;
  portraitRequired: boolean;
  source: string;
  anchor: { name: string; point: Point2 };
  matrix: Matrix;
  defs?: string;
}
